import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Cpu, Loader2, Briefcase, Zap, TrendingUp, ArrowRight, Check,
  ToggleLeft, ToggleRight, History, AlertCircle, Building2, Percent,
} from "lucide-react";
import { api } from "@/lib/api";

const REGIME_COLORS = {
  trending_up:     "#10b981",
  trending_down:   "#ef4444",
  ranging:         "#3b82f6",
  high_volatility: "#f59e0b",
  low_liquidity:   "#6b7280",
  news_window:     "#a855f7",
  unknown:         "#6b7280",
};

const scoreColor = (v) =>
  v >= 80 ? "#10b981" : v >= 60 ? "#3b82f6" : v >= 40 ? "#f59e0b" : "#ef4444";

const RegimePill = ({ regime }) => (
  <span
    className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider"
    style={{
      color: REGIME_COLORS[regime] || "#9ca3af",
      background: `${REGIME_COLORS[regime] || "#9ca3af"}20`,
      border: `1px solid ${REGIME_COLORS[regime] || "#9ca3af"}40`,
    }}
    data-testid={`regime-pill-${regime}`}
  >
    {(regime || "unknown").replace(/_/g, " ")}
  </span>
);

const ScoreBar = ({ value }) => {
  const color = scoreColor(value);
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-[#1f2937] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500"
             style={{ width: `${value}%`, background: color }} />
      </div>
      <div className="font-mono font-bold text-xs w-9 text-right" style={{ color }}>
        {Math.round(value)}
      </div>
    </div>
  );
};

// ---------------- Portfolio Tab ----------------

const PortfolioTab = ({ data, onApply }) => {
  if (!data) return null;
  return (
    <div className="space-y-6" data-testid="optimizer-portfolio-tab">
      {/* Global recommendations */}
      {data.global_recommendations?.length > 0 && (
        <div className="rounded-lg border border-[#3b82f6]/40 bg-[#3b82f6]/5 p-5"
             data-testid="portfolio-global-rec">
          <div className="vx-label mb-2 flex items-center gap-2 text-[#3b82f6]">
            <AlertCircle className="w-3 h-3" /> Portfolio-Wide Observations
          </div>
          <div className="space-y-2">
            {data.global_recommendations.map((r, i) => (
              <div key={i} className="text-sm text-[#e5e7eb] leading-relaxed">· {r}</div>
            ))}
          </div>
        </div>
      )}

      {/* Per-account cards */}
      {data.accounts?.length === 0 ? (
        <div className="rounded-lg border border-[#1f2937] bg-[#111827] p-8 text-center text-sm text-[#9ca3af]">
          No accounts configured yet.
        </div>
      ) : (
        <div className="space-y-4">
          {data.accounts.map((acct) => {
            const rec = acct.recommendation || {};
            const isChange = rec.action === "change_strategy";
            const isReduce = rec.action === "reduce_risk";
            return (
              <div key={acct.account_id}
                   className="rounded-lg border border-[#1f2937] bg-[#111827] p-5 space-y-4"
                   data-testid={`portfolio-account-${acct.account_id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Briefcase className="w-4 h-4 text-[#3b82f6]" />
                      <div className="font-heading font-black text-lg">{acct.name || "Account"}</div>
                      {acct.is_default && (
                        <span className="text-[9px] font-mono bg-[#3b82f6]/20 text-[#3b82f6] px-1.5 py-0.5 rounded">
                          DEFAULT
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] font-mono text-[#6b7280]">
                      {acct.broker || "—"} · Bal ${acct.balance?.toFixed(2) || "0.00"} · Eq ${acct.equity?.toFixed(2) || "0.00"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[9px] font-mono uppercase tracking-widest text-[#6b7280]">Regime Fit</div>
                    <div className="font-heading text-3xl font-black" style={{ color: scoreColor(acct.avg_fit_score) }}>
                      {acct.avg_fit_score}%
                    </div>
                  </div>
                </div>

                {/* Active strategy + symbols */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <div className="text-[9px] font-mono uppercase tracking-widest text-[#6b7280] mb-1">
                      Active Strategy
                    </div>
                    <div className="font-mono text-sm">
                      {acct.active_strategy?.display_name || <span className="text-[#6b7280]">— none —</span>}
                    </div>
                  </div>
                  <div>
                    <div className="text-[9px] font-mono uppercase tracking-widest text-[#6b7280] mb-1">
                      Symbols &amp; Regimes
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(acct.regimes || {}).map(([sym, r]) => (
                        <div key={sym} className="flex items-center gap-1">
                          <span className="font-mono text-[11px]">{sym}</span>
                          <RegimePill regime={r} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Recommendation */}
                <div className={`rounded border p-3 ${
                  isChange ? "border-[#f59e0b]/40 bg-[#f59e0b]/5" :
                  isReduce ? "border-[#ef4444]/40 bg-[#ef4444]/5" :
                  "border-[#10b981]/40 bg-[#10b981]/5"
                }`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className={`text-[10px] font-mono uppercase tracking-widest font-bold ${
                        isChange ? "text-[#f59e0b]" :
                        isReduce ? "text-[#ef4444]" :
                        "text-[#10b981]"
                      }`}>
                        {rec.action?.replace(/_/g, " ") || "keep"}
                      </div>
                      <div className="text-sm text-[#e5e7eb] mt-1 leading-relaxed">{rec.reason}</div>
                    </div>
                    {isChange && (
                      <button
                        onClick={() => onApply(acct.account_id, rec.new_profile_id, rec.new_display_name)}
                        className="text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded bg-[#f59e0b] text-[#0a0e1a] hover:bg-[#fbbf24] transition-colors font-bold"
                        data-testid={`apply-strategy-${acct.account_id}`}
                      >
                        Apply →
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------------- Dynamic Tab ----------------

const DynamicTab = ({ data, onApply, autoSwitch, onToggleAuto, switches }) => {
  if (!data) return null;
  return (
    <div className="space-y-6" data-testid="optimizer-dynamic-tab">
      {/* Auto-switch toggle */}
      <div className="rounded-lg border border-[#1f2937] bg-[#111827] p-5 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="vx-label mb-1 flex items-center gap-2">
            <Zap className="w-3 h-3 text-[#a855f7]" /> Auto-Switch Mode
          </div>
          <div className="text-sm text-[#9ca3af] max-w-xl leading-relaxed">
            When enabled, the Dynamic Strategy Engine will automatically swap to the
            highest-scoring strategy when its regime fit is ≥15 points better than the current one.
          </div>
        </div>
        <button
          onClick={() => onToggleAuto(!autoSwitch)}
          className="flex items-center gap-2 px-4 py-2 rounded font-mono text-xs uppercase tracking-widest transition-colors"
          style={{
            background: autoSwitch ? "#a855f7" : "#1f2937",
            color: autoSwitch ? "#0a0e1a" : "#9ca3af",
          }}
          data-testid="dynamic-auto-toggle"
        >
          {autoSwitch ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
          {autoSwitch ? "AUTO ON" : "AUTO OFF"}
        </button>
      </div>

      {/* Active strategy */}
      {data.active_strategy && (
        <div className="rounded-lg border border-[#3b82f6]/40 bg-[#3b82f6]/5 p-4 flex items-center gap-3"
             data-testid="dynamic-active-strategy">
          <TrendingUp className="w-4 h-4 text-[#3b82f6]" />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#3b82f6] font-bold">Currently Running</div>
            <div className="font-mono text-sm mt-0.5">{data.active_strategy.display_name}</div>
          </div>
        </div>
      )}

      {/* Per-symbol strategy scores */}
      <div>
        <div className="vx-label mb-3">Strategy Fit by Symbol · Current Regime</div>
        {data.per_symbol?.length === 0 ? (
          <div className="rounded-lg border border-[#1f2937] bg-[#111827] p-8 text-center text-sm text-[#9ca3af]">
            No symbols traded yet.
          </div>
        ) : (
          <div className="space-y-4">
            {data.per_symbol.map((s) => (
              <div key={s.symbol} className="rounded-lg border border-[#1f2937] bg-[#111827] p-5"
                   data-testid={`dynamic-symbol-${s.symbol}`}>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className="font-mono font-bold text-lg">{s.symbol}</div>
                    <RegimePill regime={s.regime} />
                  </div>
                  <div className="text-[10px] font-mono text-[#6b7280] max-w-md truncate">{s.regime_reason}</div>
                </div>
                <div className="space-y-2">
                  {s.recommendations?.map((r) => (
                    <div key={r.profile_id}
                         className={`grid grid-cols-12 gap-3 items-center px-3 py-2 rounded ${
                           r.is_active ? "bg-[#3b82f6]/10 border border-[#3b82f6]/30" : "hover:bg-[#0f1626]"
                         }`}
                         data-testid={`strategy-rec-${s.symbol}-${r.profile_id}`}>
                      <div className="col-span-4 flex items-center gap-2">
                        {r.is_active && <Check className="w-3 h-3 text-[#3b82f6]" />}
                        <div>
                          <div className={`font-mono text-xs ${r.is_active ? "text-[#3b82f6] font-bold" : "text-[#e5e7eb]"}`}>
                            {r.display_name}
                          </div>
                          <div className="text-[9px] font-mono text-[#6b7280]">
                            min-conf {r.min_confidence} · RR 1:{r.rr_target}
                          </div>
                        </div>
                      </div>
                      <div className="col-span-6">
                        <ScoreBar value={r.score} />
                      </div>
                      <div className="col-span-2 flex justify-end">
                        {!r.is_active && (
                          <button
                            onClick={() => onApply(r.profile_id, r.display_name)}
                            className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border border-[#1f2937] hover:border-[#3b82f6] hover:text-[#3b82f6] transition-colors"
                            data-testid={`switch-${s.symbol}-${r.profile_id}`}
                          >
                            Switch →
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent switches audit log */}
      {switches && switches.length > 0 && (
        <div className="rounded-lg border border-[#1f2937] bg-[#111827]" data-testid="dynamic-switches-log">
          <div className="px-5 py-3 border-b border-[#1f2937] vx-label flex items-center gap-2">
            <History className="w-3 h-3" /> Recent Strategy Switches
          </div>
          {switches.map((sw, i) => (
            <div key={i} className="grid grid-cols-4 gap-3 px-5 py-2 border-b border-[#1f2937] text-xs">
              <div className="font-mono text-[#9ca3af]">
                {sw.switched_at ? new Date(sw.switched_at).toLocaleString() : ""}
              </div>
              <div className="font-mono">{sw.display_name || sw.profile_id}</div>
              <div className={`font-mono ${sw.source === "auto_switch" ? "text-[#a855f7]" : "text-[#9ca3af]"}`}>
                {sw.source?.replace(/_/g, " ") || "manual"}
              </div>
              <div className="font-mono text-right">
                {sw.score_before !== undefined && (
                  <>
                    <span className="text-[#6b7280]">{sw.score_before}</span>
                    <ArrowRight className="w-3 h-3 inline mx-1 text-[#6b7280]" />
                    <span className="text-[#10b981]">{sw.score_after}</span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------- Broker Routing Tab ----------------

const BrokerRoutingTab = ({ data }) => {
  if (!data) return null;
  if (data.status === "no_trades") {
    return (
      <div className="rounded-lg border border-[#1f2937] bg-[#111827] p-8 text-center" data-testid="broker-routing-empty">
        <Building2 className="w-8 h-8 text-[#6b7280] mx-auto mb-3" />
        <div className="text-sm text-[#9ca3af]">{data.message}</div>
      </div>
    );
  }
  const singleBroker = data.status === "single_broker";
  return (
    <div className="space-y-6" data-testid="optimizer-broker-tab">
      {/* Broker summary strip */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {(data.brokers || []).map((b) => (
          <div key={b.broker} className="rounded-lg border border-[#1f2937] bg-[#111827] p-4"
               data-testid={`broker-summary-${b.broker}`}>
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-[#3b82f6]" />
              <div className="font-mono font-bold text-sm">{b.broker || "Unknown"}</div>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] font-mono">
              <div className="text-[#6b7280]">Trades</div>       <div className="text-right">{b.trades}</div>
              <div className="text-[#6b7280]">Win Rate</div>     <div className="text-right text-[#3b82f6]">{b.win_rate_pct}%</div>
              <div className="text-[#6b7280]">Net P&amp;L</div>  <div className={`text-right ${b.net_pnl >= 0 ? "text-[#10b981]" : "text-[#ef4444]"}`}>
                ${b.net_pnl.toFixed(2)}
              </div>
              <div className="text-[#6b7280]">Avg Slip</div>     <div className="text-right">
                {b.avg_slippage !== null && b.avg_slippage !== undefined
                  ? b.avg_slippage.toFixed(5)
                  : "—"}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Single-broker helpful hint */}
      {singleBroker && (
        <div className="rounded-lg border border-[#3b82f6]/40 bg-[#3b82f6]/5 p-4 flex items-start gap-3" data-testid="broker-single-hint">
          <AlertCircle className="w-4 h-4 text-[#3b82f6] mt-0.5" />
          <div className="text-sm text-[#e5e7eb] leading-relaxed">
            You only have one broker configured. Add a second account with a different broker to
            unlock routing recommendations comparing win-rate + slippage per symbol.
          </div>
        </div>
      )}

      {/* Recommendations */}
      {data.recommendations?.length > 0 && (
        <div className="space-y-3" data-testid="broker-recommendations">
          <div className="vx-label flex items-center gap-2">
            <Percent className="w-3 h-3 text-[#f59e0b]" /> Actionable Routing Recommendations
          </div>
          {data.recommendations.map((r, i) => (
            <div key={i} className="rounded-lg border border-[#f59e0b]/40 bg-[#f59e0b]/5 p-4"
                 data-testid={`broker-rec-${r.symbol}`}>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="font-mono font-bold text-lg">{r.symbol}</div>
                <div className="flex items-center gap-2 text-xs font-mono">
                  <span className="text-[#ef4444] line-through">{r.avoid_broker}</span>
                  <ArrowRight className="w-3 h-3 text-[#f59e0b]" />
                  <span className="text-[#10b981] font-bold">{r.recommended_broker}</span>
                </div>
              </div>
              <div className="text-sm text-[#e5e7eb] leading-relaxed">{r.reason}</div>
            </div>
          ))}
        </div>
      )}

      {/* Per-symbol × broker matrix */}
      {data.per_symbol?.length > 0 && (
        <div className="rounded-lg border border-[#1f2937] bg-[#111827]" data-testid="broker-matrix">
          <div className="px-5 py-3 border-b border-[#1f2937] vx-label flex items-center justify-between">
            <span>Per-Symbol × Broker Matrix</span>
            <span className="text-[10px] font-mono text-[#6b7280] normal-case">
              Min {data.min_trades_for_recommendation} trades for a recommendation
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#1f2937] text-[#6b7280]">
                  <th className="text-left px-4 py-2 font-mono uppercase tracking-widest">Symbol</th>
                  {(data.brokers || []).map(b => (
                    <th key={b.broker} className="text-right px-4 py-2 font-mono uppercase tracking-widest">
                      {b.broker || "?"}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.per_symbol.map(row => (
                  <tr key={row.symbol} className="border-b border-[#1f2937] last:border-0" data-testid={`broker-row-${row.symbol}`}>
                    <td className="px-4 py-2 font-mono font-bold">{row.symbol}</td>
                    {(data.brokers || []).map(b => {
                      const cell = row.brokers[b.broker];
                      if (!cell) return <td key={b.broker} className="text-right px-4 py-2 text-[#6b7280]">—</td>;
                      return (
                        <td key={b.broker} className="text-right px-4 py-2 font-mono">
                          <div className="text-[#e5e7eb]">{cell.win_rate_pct}%</div>
                          <div className="text-[9px] text-[#6b7280]">{cell.trades} trades</div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------- Main page ----------------

export default function Optimizer() {
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("portfolio");
  const [portfolio, setPortfolio] = useState(null);
  const [dynamic, setDynamic] = useState(null);
  const [brokerRouting, setBrokerRouting] = useState(null);
  const [switches, setSwitches] = useState([]);
  const [autoSwitch, setAutoSwitch] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, d, s, br] = await Promise.all([
        api.get("/optimizer/portfolio"),
        api.get("/optimizer/dynamic-strategy"),
        api.get("/optimizer/switches?limit=15"),
        api.get("/optimizer/broker-routing"),
      ]);
      setPortfolio(p.data);
      setDynamic(d.data);
      setSwitches(s.data?.switches || []);
      setBrokerRouting(br.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load optimizer");
    } finally {
      setLoading(false);
    }
  }, []);

  const applyStrategy = async (profileId, displayName) => {
    try {
      await api.post(`/optimizer/apply-strategy?profile_id=${encodeURIComponent(profileId)}`);
      toast.success(`Switched to ${displayName}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Switch failed");
    }
  };

  const applyPortfolioSwitch = async (_accountId, profileId, displayName) => {
    // For MVP we apply on the default/current account since resolve_account uses that
    await applyStrategy(profileId, displayName);
  };

  const toggleAuto = async (enabled) => {
    try {
      await api.post(`/optimizer/auto-switch?enabled=${enabled}`);
      setAutoSwitch(enabled);
      toast.success(enabled ? "Auto-switch enabled" : "Auto-switch disabled");
      // Run an immediate tick to potentially switch now
      if (enabled) {
        const r = await api.post("/optimizer/auto-switch/tick");
        if (r.data?.switched) {
          toast.success(`Auto-switched to ${r.data.result.display_name}`);
          await load();
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load]);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]" data-testid="optimizer-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#3b82f6]" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="optimizer-page">
      {/* Header */}
      <div>
        <div className="vx-label mb-1 flex items-center gap-2">
          <Cpu className="w-3 h-3" /> Portfolio & Dynamic Strategy Optimizer
        </div>
        <h1 className="font-heading text-3xl lg:text-4xl font-black tracking-tighter">
          Right Strategy. Right Place. Right Now.
        </h1>
        <p className="text-sm text-[#9ca3af] mt-2 max-w-2xl leading-relaxed">
          <span className="text-[#e5e7eb] font-bold">Portfolio Intelligence</span> looks
          across all your accounts and recommends the best strategy assignment.{" "}
          <span className="text-[#e5e7eb] font-bold">Dynamic Strategy Engine</span> watches
          each symbol&apos;s regime and switches to the best-fit strategy the moment conditions change.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#1f2937]" data-testid="optimizer-tabs">
        <button
          onClick={() => setTab("portfolio")}
          className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border-b-2 -mb-px transition-colors ${
            tab === "portfolio"
              ? "border-[#3b82f6] text-[#3b82f6]"
              : "border-transparent text-[#9ca3af] hover:text-[#e5e7eb]"
          }`}
          data-testid="tab-portfolio"
        >
          <Briefcase className="w-3 h-3 inline mr-1.5" /> Portfolio Intelligence
        </button>
        <button
          onClick={() => setTab("dynamic")}
          className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border-b-2 -mb-px transition-colors ${
            tab === "dynamic"
              ? "border-[#a855f7] text-[#a855f7]"
              : "border-transparent text-[#9ca3af] hover:text-[#e5e7eb]"
          }`}
          data-testid="tab-dynamic"
        >
          <Zap className="w-3 h-3 inline mr-1.5" /> Dynamic Strategy Engine
        </button>
        <button
          onClick={() => setTab("broker")}
          className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border-b-2 -mb-px transition-colors ${
            tab === "broker"
              ? "border-[#f59e0b] text-[#f59e0b]"
              : "border-transparent text-[#9ca3af] hover:text-[#e5e7eb]"
          }`}
          data-testid="tab-broker"
        >
          <Building2 className="w-3 h-3 inline mr-1.5" /> Broker Routing
        </button>
      </div>

      {tab === "portfolio"
        ? <PortfolioTab data={portfolio} onApply={applyPortfolioSwitch} />
        : tab === "dynamic"
        ? <DynamicTab data={dynamic} onApply={applyStrategy}
                      autoSwitch={autoSwitch} onToggleAuto={toggleAuto}
                      switches={switches} />
        : <BrokerRoutingTab data={brokerRouting} />}
    </div>
  );
}
